import React from 'react';
import { ActivityIcon, GraduationCapIcon, PhoneIcon, RouteIcon, UsersRoundIcon } from 'lucide-react';
import { CONTACT } from '../../data/modalities';
type ReviewFactor = {
  label: string;
  priority: 'Priority 1' | 'Priority 2';
  icon: typeof UsersRoundIcon;
  points: string[];
};
const REVIEW_FACTORS: ReviewFactor[] = [{
  label: 'Patient Needs',
  priority: 'Priority 1',
  icon: UsersRoundIcon,
  points: ['Pain', 'Inflammation', 'Muscle re-education']
}, {
  label: 'Practice Workflow',
  priority: 'Priority 1',
  icon: ActivityIcon,
  points: ['Open treatment room', 'Closed treatment room', 'Attended / unattended treatment', 'Clothed / unclothed treatment']
}, {
  label: 'Product Training',
  priority: 'Priority 2',
  icon: GraduationCapIcon,
  points: ['In-office', 'Zoom / video calls', 'Online resources']
}, {
  label: 'Implementation Plan',
  priority: 'Priority 2',
  icon: RouteIcon,
  points: ['Pre-launch', 'Email blasts', 'Social media', 'Open house']
}];
export function WelcomeVideo() {
  return <section className="before-you-buy w-full overflow-hidden bg-white px-4 py-20 sm:px-0 sm:py-24" data-id="element-778" data-mp="5r81ehuombqa">
      <div className="before-you-buy__inner mx-auto grid w-full max-w-[358px] items-start gap-10 sm:max-w-7xl sm:px-8 lg:grid-cols-[0.72fr_1.28fr] lg:gap-12" data-id="element-779" data-mp="akavqs7a9p7j">
        <div className="before-you-buy__copy w-full min-w-0 lg:sticky lg:top-28" data-id="element-780" data-mp="3dzyfxdkegq06">
          <h2 className="before-you-buy__heading w-full max-w-full whitespace-normal break-words font-display text-[30px] font-bold leading-[1.08] tracking-tight text-navy sm:max-w-xl sm:text-[2.3rem] sm:leading-tight" data-id="element-781" data-mp="3e22visrnvdga">
            The Right Equipment = Better Outcomes
          </h2>
          <p className="before-you-buy__copy-text mt-5 w-full max-w-full text-[16px] leading-relaxed text-charcoal/75 sm:max-w-md" data-id="element-782" data-mp="3suw9g9rabliu">
            A quick conversation can help a practice avoid investing in
            technology that does not match its patients, workflow, budget, or
            training capacity. JAG Medical Solutions helps doctors compare
            modalities, understand implementation needs, and choose equipment
            with a clear plan for adoption.
          </p>
          <a href={CONTACT.phoneHref} className="before-you-buy__cta mt-8 inline-flex max-w-full items-center gap-2 rounded-full bg-navy px-7 py-3.5 text-[15px] font-semibold text-white transition-colors hover:bg-navy/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal focus-visible:ring-offset-2" data-id="element-783" data-mp="3d9rwtl8f4k4x">
            <PhoneIcon className="h-[18px] w-[18px] shrink-0" data-id="element-784" data-mp="2ubyt4gpz066h" />
            Book a Call
          </a>
        </div>

        <aside className="before-you-buy__card mx-auto w-full min-w-0 rounded-3xl border border-slate-200 bg-white shadow-[0_28px_65px_-30px_rgba(26,27,75,0.38)] sm:max-w-none" data-id="element-785" data-mp="2pk7101r61se8">
          <div className="before-you-buy__card-body p-5 sm:p-8" data-id="element-786" data-mp="1xum3ly7wyjxw">
            <div className="h-1 w-10 rounded-full bg-teal" aria-hidden="true" data-id="element-787" data-mp="1xrzu3t1gpud1" />
            <h3 className="mt-5 font-display text-2xl font-bold leading-tight text-navy sm:text-[1.7rem]" data-id="element-788" data-mp="1nokqv8knd7t2">
              <span className="block" data-id="element-789" data-mp="1siozdw9k9nw9">The</span>
              <span className="block" data-id="element-790" data-mp="1dshj9n710gyq">JAG Medical Solutions Standard</span>
            </h3>
            <p className="mt-3 max-w-full text-[15px] leading-relaxed text-charcoal/70 sm:max-w-xl sm:text-[16px]" data-id="element-791" data-mp="1iq9h0xucqxau">
              Below is our criteria used to evaluate any equipment before
              it&apos;s implemented into your practice.
            </p>

            <div className="before-you-buy__factors mt-6 grid gap-3 sm:grid-cols-2" data-id="element-792" data-mp="2bd43u1en68rh">
              {REVIEW_FACTORS.map(({
              label,
              priority,
              icon: Icon,
              points
            }) => <div key={label} className="before-you-buy__factor group flex min-w-0 flex-col rounded-2xl border border-slate-100 bg-slate-50 px-5 py-5 transition-[transform,border-color,background-color,box-shadow] duration-200 hover:-translate-y-0.5 hover:border-teal/40 hover:bg-teal/[0.04] hover:shadow-sm" data-id="element-793" data-mp="21fpl1v58x7w0">
                  <div className="flex items-center gap-3" data-id="element-794" data-mp="11i9ykgp3rqjm">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-teal/10 text-teal transition-colors duration-200 group-hover:bg-teal group-hover:text-white" data-id="element-795" data-mp="11gp4es6pa9ma">
                      <Icon className="h-[18px] w-[18px]" data-id="element-796" data-mp="rij3acspx7ih" />
                    </span>
                    <div className="min-w-0" data-id="element-797" data-mp="1p7coebtw4pp7">
                      <p className="text-[9.5px] font-semibold uppercase tracking-[0.14em] text-charcoal/40" data-id="element-798" data-mp="1k8hzcs5by4wn">
                        {priority}
                      </p>
                      <p className="min-w-0 text-[15px] font-bold leading-snug text-navy" data-id="element-799" data-mp="1fbrjo5xftbbr">
                        {label}
                      </p>
                    </div>
                  </div>

                  <ul className="mt-4 space-y-2 border-t border-slate-200/80 pt-4" data-id="element-800" data-mp="3sf2ed6wzlv6">
                    {points.map(point => <li key={point} className="flex items-start gap-2.5 text-[14.5px] leading-[1.5] text-charcoal/80" data-id="element-801" data-mp="2ajjxu4psb4o5">
                        <span className="mt-[8px] h-1.5 w-1.5 shrink-0 rounded-full bg-teal/70" aria-hidden="true" data-id="element-802" data-mp="2kgps1ltp3bsl" />
                        <span className="min-w-0" data-id="element-803" data-mp="2u7aw3os518iw">{point}</span>
                      </li>)}
                  </ul>
                </div>)}
            </div>
          </div>
        </aside>
      </div>
    </section>;
}